<template>
  <div class="sketch-entities-transform-property" data-testid="sketch-copy-entities-property-section">
    <n-collapse :default-expanded-names="['entities', 'params']" class="pm-collapse">
      <n-collapse-item title="要复制的实体(E)" name="entities">
        <div class="section-body">
          <div
            class="selection-box"
            data-testid="copy-entities-selection-box"
            @click="activateSelection(SketchEntitiesTransformSelectionRole.Entities)"
          >
            <SelectionListFrame
              :class="{ 'selection-list-frame--active': isEntitiesSelectionActive }"
              :items="selectedItems"
              :active-index="isEntitiesSelectionActive ? 0 : -1"
              :item-key-field="'id'"
              :item-label-field="'label'"
              :visible-rows="4"
              :context-menu-enabled="true"
              :show-delete-all-action="true"
              :show-delete-action="true"
              empty-text="请在视口中选择要复制的实体"
              @delete-item="handleDeleteItem"
              @delete-all="handleDeleteAll"
            >
              <template #item="{ item }">
                <div class="entity-main">
                  <n-text>{{ resolveSelectionItemLabel(item) }}</n-text>
                </div>
              </template>
            </SelectionListFrame>
          </div>
        </div>
      </n-collapse-item>

      <n-collapse-item title="参数(P)" name="params">
        <div class="section-body">
          <n-radio-group :value="state.inputMode" @update:value="handleInputModeChange">
            <div class="radio-list">
              <n-radio :value="SketchEntitiesTransformInputMode.FromTo">从/到(F)</n-radio>
              <n-radio :value="SketchEntitiesTransformInputMode.XY">X/Y</n-radio>
            </div>
          </n-radio-group>

          <template v-if="state.inputMode === SketchEntitiesTransformInputMode.FromTo">
            <div class="selection-row">
              <span class="field-label">起点</span>
              <div
                class="reference-list"
                data-testid="copy-from-point-box"
                @click="activateSelection(SketchEntitiesTransformSelectionRole.FromPoint)"
              >
                <SelectionListFrame
                  :class="{
                    'selection-list-frame--single-row': true,
                    'selection-list-frame--active': isFromPointSelectionActive,
                  }"
                  :items="fromPointItems"
                  :active-index="isFromPointSelectionActive ? 0 : -1"
                  :visible-rows="1"
                  :context-menu-enabled="false"
                  :show-delete-action="false"
                  :show-delete-all-action="false"
                  :item-key-field="'id'"
                  :item-label-field="'label'"
                  empty-text="请选择起点"
                  list-role="list"
                >
                  <template #item="{ item }">
                    <div class="entity-main">
                      <n-text>{{ resolveSelectionItemLabel(item) }}</n-text>
                    </div>
                  </template>
                </SelectionListFrame>
              </div>
            </div>
          </template>

          <template v-else>
            <div class="coordinate-row">
              <span class="coordinate-label">X</span>
              <n-input-number
                :value="targetXDisplay"
                size="small"
                :precision="6"
                :step="1"
                @update:value="(value) => updateCoordinate('x', value)"
              />
            </div>
            <div class="coordinate-row">
              <span class="coordinate-label">Y</span>
              <n-input-number
                :value="targetYDisplay"
                size="small"
                :precision="6"
                :step="1"
                @update:value="(value) => updateCoordinate('y', value)"
              />
            </div>
          </template>

          <n-button
            v-if="state.inputMode === SketchEntitiesTransformInputMode.XY"
            size="small"
            class="repeat-button"
            :disabled="!state.canRepeat"
            data-testid="copy-repeat-button"
            @click="repeatTransform"
          >
            重复(P)
          </n-button>
        </div>
      </n-collapse-item>
    </n-collapse>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import type { Subscription } from 'rxjs';
import { NButton, NCollapse, NCollapseItem, NInputNumber, NRadio, NRadioGroup, NText } from 'naive-ui';
import {
  SketchEntitiesTransformCommand,
  SketchEntitiesTransformInputMode,
  SketchEntitiesTransformRelationRetentionMode,
  SketchEntitiesTransformSelectionRole,
  type PropertyManagerContext,
  type SketchEntitiesTransformState,
} from '@/core';
import SelectionListFrame from '@/components/editor/SelectionListFrame.vue';
import { useViewer } from '@/hooks';
import {
  convertMToMm,
  convertMmToM,
  SketchEntitiesTransformInputMode as CadSketchEntitiesTransformInputMode,
  SketchEntitiesTransformRelationRetentionMode as CadSketchEntitiesTransformRelationRetentionMode,
  SketchEntitiesTransformSelectionRole as CadSketchEntitiesTransformSelectionRole,
  type SketchCopyEntitiesController,
  type SketchEntitiesTransformControllerSnapshot,
} from '@puppycad/cad-core';

/**
 * 复制实体属性面板参数。
 */
interface SketchCopyEntitiesPropertySectionProps {
  /** PropertyManager 上下文。 */
  context: PropertyManagerContext;
}

const props = defineProps<SketchCopyEntitiesPropertySectionProps>();
const { getViewer, setViewerCallback } = useViewer();

/**
 * 创建复制实体面板默认状态。
 *
 * @returns 复制实体面板默认状态。
 */
function createDefaultState(): SketchEntitiesTransformState {
  return {
    active: false,
    command: SketchEntitiesTransformCommand.Copy,
    inputMode: SketchEntitiesTransformInputMode.FromTo,
    activeSelectionRole: SketchEntitiesTransformSelectionRole.Entities,
    relationRetentionMode: SketchEntitiesTransformRelationRetentionMode.BreakExternalRelations,
    selectedItems: [],
    fromPoint: null,
    toPoint: null,
    rotationCenter: null,
    targetX: 0,
    targetY: 0,
    angleDeg: null,
    canConfirm: false,
    canRepeat: false,
  };
}

/** 当前面板本地状态。 */
const localState = ref<SketchEntitiesTransformState>(createDefaultState());

/** 控制器快照订阅。 */
let snapshotSubscription: Subscription | null = null;

/** Viewer 就绪回调取消函数。 */
let disposeViewerReadyCallback: (() => void) | null = null;

/** 当前复制实体命令状态。 */
const state = computed(() => localState.value);

/** 当前已选实体列表。 */
const selectedItems = computed(() => state.value.selectedItems ?? []);

/** 当前起点列表项。 */
const fromPointItems = computed(() => state.value.fromPoint ? [state.value.fromPoint] : []);

/** 当前“要复制的实体”输入框是否激活。 */
const isEntitiesSelectionActive = computed<boolean>(() => (
  state.value.activeSelectionRole === SketchEntitiesTransformSelectionRole.Entities
));

/** 当前“起点”输入框是否激活。 */
const isFromPointSelectionActive = computed<boolean>(() => (
  state.value.activeSelectionRole === SketchEntitiesTransformSelectionRole.FromPoint
));

/** X 方向复制偏移的毫米显示值。 */
const targetXDisplay = computed(() => Number(convertMToMm(state.value.targetX).toFixed(6)));
/** Y 方向复制偏移的毫米显示值。 */
const targetYDisplay = computed(() => Number(convertMToMm(state.value.targetY).toFixed(6)));

/**
 * 解析选择列表项显示文本。
 *
 * @param item SelectionListFrame 插槽项。
 * @returns 可显示的标签文本。
 */
function resolveSelectionItemLabel(item: unknown): string {
  if (typeof item !== 'object' || item === null || !('label' in item)) {
    return '';
  }

  const label = (item as { label?: unknown }).label;
  return typeof label === 'string' ? label : '';
}

/**
 * 获取复制实体控制器。
 *
 * @returns 控制器实例。
 */
function getCopyEntitiesController(): SketchCopyEntitiesController | null {
  return getViewer()?.sketchCopyEntitiesController ?? null;
}

/**
 * 将 cad-core 输入模式映射为 PropertyManager 输入模式。
 *
 * @param mode cad-core 输入模式。
 * @returns PropertyManager 输入模式。
 */
function mapInputModeFromCad(mode: CadSketchEntitiesTransformInputMode): SketchEntitiesTransformInputMode {
  return mode === CadSketchEntitiesTransformInputMode.FromTo
    ? SketchEntitiesTransformInputMode.FromTo
    : SketchEntitiesTransformInputMode.XY;
}

/**
 * 将 PropertyManager 输入模式映射为 cad-core 输入模式。
 *
 * @param mode PropertyManager 输入模式。
 * @returns cad-core 输入模式。
 */
function mapInputModeToCad(mode: SketchEntitiesTransformInputMode): CadSketchEntitiesTransformInputMode {
  return mode === SketchEntitiesTransformInputMode.FromTo
    ? CadSketchEntitiesTransformInputMode.FromTo
    : CadSketchEntitiesTransformInputMode.Coordinate;
}

/**
 * 将 cad-core 选择角色映射为 PropertyManager 选择角色。
 *
 * @param role cad-core 选择角色。
 * @returns PropertyManager 选择角色。
 */
function mapSelectionRoleFromCad(
  role: CadSketchEntitiesTransformSelectionRole,
): SketchEntitiesTransformSelectionRole {
  if (role === CadSketchEntitiesTransformSelectionRole.FromPoint) {
    return SketchEntitiesTransformSelectionRole.FromPoint;
  }
  if (role === CadSketchEntitiesTransformSelectionRole.ToPoint) {
    return SketchEntitiesTransformSelectionRole.ToPoint;
  }
  if (role === CadSketchEntitiesTransformSelectionRole.RotationCenter) {
    return SketchEntitiesTransformSelectionRole.RotationCenter;
  }
  return SketchEntitiesTransformSelectionRole.Entities;
}

/**
 * 将控制器快照映射为面板状态。
 *
 * @param snapshot 控制器快照。
 * @returns 面板状态。
 */
function mapSnapshotToState(snapshot: SketchEntitiesTransformControllerSnapshot): SketchEntitiesTransformState {
  return {
    active: snapshot.active,
    command: snapshot.command,
    inputMode: mapInputModeFromCad(snapshot.inputMode),
    activeSelectionRole: mapSelectionRoleFromCad(snapshot.activeSelectionRole),
    relationRetentionMode: snapshot.relationRetentionMode === CadSketchEntitiesTransformRelationRetentionMode.BreakExternalRelations
      ? SketchEntitiesTransformRelationRetentionMode.BreakExternalRelations
      : SketchEntitiesTransformRelationRetentionMode.KeepRelations,
    selectedItems: snapshot.selectedItems.map((item) => ({
      id: item.id,
      label: item.label,
    })),
    fromPoint: snapshot.fromPoint
      ? {
        id: snapshot.fromPoint.id,
        label: snapshot.fromPoint.label,
      }
      : null,
    toPoint: snapshot.toPoint
      ? {
        id: snapshot.toPoint.id,
        label: snapshot.toPoint.label,
      }
      : null,
    rotationCenter: snapshot.rotationCenter
      ? {
        id: snapshot.rotationCenter.id,
        label: snapshot.rotationCenter.label,
      }
      : null,
    targetX: snapshot.targetX,
    targetY: snapshot.targetY,
    angleDeg: snapshot.angleDeg,
    canConfirm: snapshot.canConfirm,
    canRepeat: snapshot.canRepeat,
  };
}

/**
 * 从复制实体控制器同步最新快照到本地状态。
 */
function syncFromController(): void {
  const snapshot = getCopyEntitiesController()?.getSnapshot?.() ?? null;
  if (!snapshot) {
    localState.value = props.context.sketchEntitiesTransformState ?? createDefaultState();
    props.context.syncSketchEntitiesTransformState?.(localState.value);
    return;
  }

  localState.value = mapSnapshotToState(snapshot);
  props.context.syncSketchEntitiesTransformState?.(localState.value);
}

/**
 * 建立复制实体控制器快照订阅。
 */
function bindControllerSnapshot(): void {
  snapshotSubscription?.unsubscribe();
  snapshotSubscription = getViewer()?.observeSketchCopyEntitiesSnapshot?.().subscribe((snapshot) => {
    const wasActive = localState.value.active;
    localState.value = mapSnapshotToState(snapshot);
    props.context.syncSketchEntitiesTransformState?.(localState.value);
    //------ 复制实体面板退出同步 开始------
    /**
     * 视口落点提交由 cad-core 内部触发，不经过 PropertyManager 顶部按钮。
     * 控制器清理后需要通知宿主退出命令态，避免右侧面板残留。
     */
    if (wasActive && !snapshot.active) {
      props.context.cancelSketchEntitiesTransform?.();
    }
    //------ 复制实体面板退出同步 结束------
  }) ?? null;
}

onMounted(() => {
  syncFromController();
  bindControllerSnapshot();
  disposeViewerReadyCallback = setViewerCallback(() => {
    syncFromController();
    bindControllerSnapshot();
  });
});

onBeforeUnmount(() => {
  snapshotSubscription?.unsubscribe();
  snapshotSubscription = null;
  disposeViewerReadyCallback?.();
  disposeViewerReadyCallback = null;
});

/**
 * 激活指定选择角色。
 *
 * @param role 目标选择角色。
 */
function activateSelection(role: SketchEntitiesTransformSelectionRole): void {
  getCopyEntitiesController()?.setActiveSelectionRole?.(role);
  syncFromController();
}

/**
 * 切换复制实体输入模式。
 *
 * @param value 输入模式。
 */
function handleInputModeChange(value: string | number): void {
  getCopyEntitiesController()?.setInputMode?.(mapInputModeToCad(value as SketchEntitiesTransformInputMode));
  syncFromController();
}

/**
 * 更新 X/Y 平移距离。
 *
 * @param axis 输入轴。
 * @param value 输入值。
 */
function updateCoordinate(axis: 'x' | 'y', value: number | null): void {
  // UI 输入值以毫米为单位，写入 cad-core 前转换为 Three.js 米制单位。
  getCopyEntitiesController()?.setTargetCoordinate?.(axis, value === null ? null : convertMmToM(value));
  syncFromController();
}

/**
 * 处理删除单个实体项。
 *
 * @param payload SelectionListFrame delete-item 事件。
 */
function handleDeleteItem(payload: { item: unknown; index: number }): void {
  const item = payload.item as { id: string };
  if (item?.id) {
    getCopyEntitiesController()?.removeSelectedEntity?.(item.id);
    syncFromController();
  }
}

/**
 * 处理删除全部实体项。
 */
function handleDeleteAll(): void {
  for (const item of selectedItems.value) {
    getCopyEntitiesController()?.removeSelectedEntity?.(item.id);
  }
  syncFromController();
}

/**
 * 重复上一次复制。
 */
function repeatTransform(): void {
  getCopyEntitiesController()?.repeat?.();
  syncFromController();
}
</script>

<style scoped>
.sketch-entities-transform-property {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.section-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.selection-row {
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr);
  align-items: center;
  gap: 6px;
}

.selection-box {
  cursor: pointer;
}

.selection-box--single {
  display: flex;
  align-items: center;
}

.entity-main {
  display: flex;
  min-width: 0;
  align-items: center;
}

.reference-list {
  min-width: 0;
  cursor: pointer;
}

.reference-list :deep(.selection-list-frame--single-row) {
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.reference-list :deep(.selection-list-frame--single-row::-webkit-scrollbar) {
  width: 0;
  height: 0;
}

.selection-box :deep(.selection-list-frame--active),
.reference-list :deep(.selection-list-frame--active) {
  border-color: var(--n-primary-color, var(--puppy-primary));
  background: color-mix(in srgb, var(--n-primary-color, var(--puppy-primary)) 8%, transparent);
}

.radio-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-label,
.coordinate-label {
  color: var(--n-text-color-2);
  font-size: 12px;
}

.coordinate-row {
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr);
  align-items: center;
  gap: 8px;
}

.repeat-button {
  align-self: center;
  min-width: 96px;
}
</style>
